import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import type { StepProps } from '../../../types/enrollment.types';

interface ContactInfoStepProps extends StepProps {
  onPhoneBlur?: () => void;
  onEmailBlur?: () => void;
  isCheckingPhone?: boolean;
  isCheckingEmail?: boolean;
}

export default function ContactInfoStep({
  formData,
  errors,
  onChange,
  onPhoneBlur,
  onEmailBlur,
  isCheckingPhone,
  isCheckingEmail,
}: ContactInfoStepProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('step.contact')}
      </Typography>

      {/* Phone (Required) */}
      <TextField
        fullWidth
        type="tel"
        label={t('field.phone')}
        value={formData.phone}
        onChange={onChange('phone')}
        onBlur={onPhoneBlur}
        error={!!errors.phone}
        helperText={errors.phone}
        inputProps={{ style: { fontSize: '1.2rem' } }}
        InputProps={{
          endAdornment: isCheckingPhone ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
        required
      />

      {/* Email (Optional) */}
      <TextField
        fullWidth
        type="email"
        label={t('field.email')}
        value={formData.email}
        onChange={onChange('email')}
        onBlur={onEmailBlur}
        error={!!errors.email}
        helperText={errors.email || t('field.email.optional')}
        inputProps={{ style: { fontSize: '1.2rem' } }}
        InputProps={{
          endAdornment: isCheckingEmail ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Referral Source */}
      <FormControl fullWidth>
        <InputLabel>{t('field.referralSource')}</InputLabel>
        <Select
          value={formData.referralSource}
          label={t('field.referralSource')}
          onChange={(e) =>
            onChange('referralSource')({ target: { value: e.target.value } })
          }
          sx={{ fontSize: '1.1rem' }}
        >
          <MenuItem value="walk_in">{t('field.referralSource.walk_in')}</MenuItem>
          <MenuItem value="referral">{t('field.referralSource.referral')}</MenuItem>
          <MenuItem value="online">{t('field.referralSource.online')}</MenuItem>
          <MenuItem value="advertisement">{t('field.referralSource.advertisement')}</MenuItem>
          <MenuItem value="social_media">{t('field.referralSource.social_media')}</MenuItem>
          <MenuItem value="other">{t('field.referralSource.other')}</MenuItem>
        </Select>
      </FormControl>

      {/* Referrer Details (Conditional) */}
      {formData.referralSource === 'referral' && (
        <>
          <TextField
            fullWidth
            label={t('field.referredBy')}
            value={formData.referredBy}
            onChange={onChange('referredBy')}
            inputProps={{ style: { fontSize: '1.2rem' } }}
          />
          <TextField
            fullWidth
            type="tel"
            label={t('field.referredByPhone')}
            value={formData.referredByPhone}
            onChange={onChange('referredByPhone')}
            inputProps={{ style: { fontSize: '1.2rem' } }}
          />
        </>
      )}
    </Box>
  );
}
